/**
 * Week attachment allowlist, size caps, and magic-byte sniffing (PLAN.md D-67).
 * Shared by client pre-check and server validation — no raw error messages leak to the API.
 */

import type { ErrorCode } from "./errors";

export const ATTACHMENT_MAX_FILES_PER_WEEK = 4;
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_COMPANY_QUOTA_BYTES = 3 * 1024 * 1024 * 1024;
export const ATTACHMENT_ZIP_MAX_BYTES = 300 * 1024 * 1024;

const ALLOWED_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".pdf",
  ".xlsx",
  ".xls",
  ".docx",
  ".doc",
  ".txt",
  ".csv",
  ".json",
  ".log",
]);

const REJECTED_EXTENSIONS = new Set([
  ".svg",
  ".html",
  ".htm",
  ".js",
  ".xml",
  ".exe",
  ".bat",
  ".cmd",
  ".sh",
  ".php",
  ".asp",
  ".aspx",
]);

export class FileValidationError extends Error {
  readonly code: ErrorCode;

  constructor(code: ErrorCode, message?: string) {
    super(message ?? code);
    this.name = "FileValidationError";
    this.code = code;
  }
}

export function sanitizeFilename(raw: string): string {
  let name = "";
  for (const char of raw) {
    const code = char.charCodeAt(0);
    if (
      code === 0 ||
      code === 0x2f ||
      code === 0x5c ||
      (code >= 0x01 && code <= 0x1f) ||
      code === 0x7f
    ) {
      continue;
    }
    name += char;
  }
  name = name.replace(/^\.+/, "").trim().replace(/\s+/g, " ");
  if (name.length > 255) name = name.slice(0, 255);
  return name.length > 0 ? name : "unnamed";
}

export function extensionOf(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return "";
  return filename.slice(lastDot).toLowerCase();
}

export function isAllowedExtension(ext: string): boolean {
  if (!ext) return false;
  const normalized = ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`;
  if (REJECTED_EXTENSIONS.has(normalized)) return false;
  return ALLOWED_EXTENSIONS.has(normalized);
}

export function allowedExtensionsAccept(): string {
  return [...ALLOWED_EXTENSIONS].join(",");
}

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  // Avoid spread of large slices — `String.fromCharCode(...64KB)` can throw RangeError.
  return new TextDecoder("latin1").decode(bytes.subarray(offset, offset + length));
}

function startsWith(bytes: Uint8Array, pattern: number[]): boolean {
  if (bytes.length < pattern.length) return false;
  return pattern.every((byte, index) => bytes[index] === byte);
}

type SniffResult = { mimeType: string; kind: string };

function sniffMagic(bytes: Uint8Array): SniffResult | null {
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return { mimeType: "image/png", kind: "png" };
  }
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
    return { mimeType: "image/jpeg", kind: "jpeg" };
  }
  if (bytes.length >= 6) {
    const gif = readAscii(bytes, 0, 6);
    if (gif === "GIF87a" || gif === "GIF89a") {
      return { mimeType: "image/gif", kind: "gif" };
    }
  }
  if (
    bytes.length >= 12 &&
    readAscii(bytes, 0, 4) === "RIFF" &&
    readAscii(bytes, 8, 4) === "WEBP"
  ) {
    return { mimeType: "image/webp", kind: "webp" };
  }
  if (bytes.length >= 4 && readAscii(bytes, 0, 4) === "%PDF") {
    return { mimeType: "application/pdf", kind: "pdf" };
  }
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) {
    const scanLen = Math.min(bytes.length, 65536);
    const latin1 = readAscii(bytes, 0, scanLen);
    if (latin1.includes("xl/")) {
      return {
        mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        kind: "xlsx",
      };
    }
    if (latin1.includes("word/")) {
      return {
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        kind: "docx",
      };
    }
    if (latin1.includes("ppt/")) {
      return { mimeType: "application/vnd.ms-powerpoint", kind: "pptx" };
    }
    return { mimeType: "application/zip", kind: "zip" };
  }
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) {
    return { mimeType: "application/x-ole-storage", kind: "ole2" };
  }
  return null;
}

const EXT_TO_KIND: Record<string, string[]> = {
  ".png": ["png"],
  ".jpg": ["jpeg"],
  ".jpeg": ["jpeg"],
  ".gif": ["gif"],
  ".webp": ["webp"],
  ".pdf": ["pdf"],
  ".xlsx": ["xlsx"],
  ".docx": ["docx"],
  ".xls": ["ole2"],
  ".doc": ["ole2"],
  ".txt": ["text"],
  ".csv": ["text"],
  ".log": ["text"],
  ".json": ["json"],
};

function isWellFormedUtf8(bytes: Uint8Array): boolean {
  try {
    const decoded = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return !decoded.includes("\0");
  } catch {
    return false;
  }
}

function sniffText(bytes: Uint8Array, ext: string): SniffResult {
  if (!isWellFormedUtf8(bytes)) {
    throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
  }
  if (ext === ".csv") {
    return { mimeType: "text/csv", kind: "text" };
  }
  return { mimeType: "text/plain", kind: "text" };
}

function sniffJson(bytes: Uint8Array): SniffResult {
  if (!isWellFormedUtf8(bytes)) {
    throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
  }
  try {
    JSON.parse(new TextDecoder("utf-8").decode(bytes));
  } catch {
    throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
  }
  return { mimeType: "application/json", kind: "json" };
}

function resolveOle2Mime(ext: string): string {
  if (ext === ".xls") return "application/vnd.ms-excel";
  if (ext === ".doc") return "application/msword";
  throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
}

export function wouldExceedQuota(currentSum: number, incoming: number, cap: number): boolean {
  return currentSum + incoming > cap;
}

export function sniffAndValidate(
  bytes: Uint8Array,
  filename: string,
): { mimeType: string; filename: string } {
  const safeName = sanitizeFilename(filename);
  const ext = extensionOf(safeName);

  if (bytes.length === 0) {
    throw new FileValidationError("INVALID_INPUT");
  }
  if (bytes.length > ATTACHMENT_MAX_BYTES) {
    throw new FileValidationError("ATTACHMENT_TOO_LARGE");
  }
  if (!isAllowedExtension(ext)) {
    throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
  }

  // SVG stored XSS — reject even if magic bytes look like PNG.
  if (ext === ".svg" || (bytes.length >= 4 && readAscii(bytes, 0, 4).toLowerCase() === "<svg")) {
    throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
  }

  const expectedKinds = EXT_TO_KIND[ext];
  if (!expectedKinds) {
    throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
  }

  let sniffed: SniffResult;
  if (expectedKinds.includes("text")) {
    sniffed = sniffText(bytes, ext);
  } else if (expectedKinds.includes("json")) {
    sniffed = sniffJson(bytes);
  } else {
    const magic = sniffMagic(bytes);
    if (!magic) {
      throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
    }
    if (magic.kind === "zip" || magic.kind === "pptx") {
      throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
    }
    if (magic.kind === "ole2") {
      if (!expectedKinds.includes("ole2")) {
        throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
      }
      sniffed = { mimeType: resolveOle2Mime(ext), kind: "ole2" };
    } else if (!expectedKinds.includes(magic.kind)) {
      throw new FileValidationError("ATTACHMENT_TYPE_REJECTED");
    } else {
      sniffed = magic;
    }
  }

  return { mimeType: sniffed.mimeType, filename: safeName };
}
